import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { API_URL } from '../../constant';
import './CaptainTCCard.css';
import fivePoundCardImg from '../../images/captain-tc/5PoundCard.jpg';
import tenPoundCardImg from '../../images/captain-tc/10PoundCard.jpg';
import twentyFivePoundCardImg from '../../images/captain-tc/25PoundCard.jpg';
import promoTopCoinIcon from "../../images/shapes/tc-units-wallet 2.png";
import promoCashIcon from "../../images/shapes/tc-cash-wallet 1.png";

function CaptainTCCard() {
    const outletContext = useOutletContext() || {};
    const { userData } = outletContext;

    const [loading, setLoading] = useState(true);
    const [cards, setCards] = useState([
        { tier: '£5', price: '£ 5.00', purchased: false, serial: null, image: fivePoundCardImg },
        { tier: '£10', price: '£ 10.00', purchased: false, serial: null, image: tenPoundCardImg },
        { tier: '£25', price: '£25.00', purchased: false, serial: null, image: twentyFivePoundCardImg },
    ]);

    // Promo wallet balances
    const [extraCardBalance, setExtraCardBalance] = useState(0);
    const [bonusCardBalance, setBonusCardBalance] = useState(0);

    const getAuthHeaders = useCallback(() => {
        const token = localStorage.getItem('auth_token');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        };
    }, []);

    const fetchCardStatus = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('auth_token');
            if (!token) return;

            const response = await fetch(`${API_URL}/api/customer/captain-tc-cards`, {
                headers: getAuthHeaders(),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.cards) {
                    setCards(prev => prev.map(card => {
                        const apiCard = data.cards.find(c =>
                            c.tier === card.tier || c.price === card.price
                        );
                        if (apiCard) {
                            return {
                                ...card,
                                purchased: apiCard.purchased || false,
                                serial: apiCard.serial || null,
                            };
                        }
                        return card;
                    }));
                }
                // Set promo wallet balances
                if (data.promo_wallet) {
                    setExtraCardBalance(data.promo_wallet.extra_card_balance || 0);
                    // setBonusCardBalance(data.promo_wallet.bonus_card_balance || 0);
                    setBonusCardBalance(0);
                }
            }
        } catch (err) {
            console.warn('Captain TC Cards API not available:', err.message);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    const handleBuy = async (card) => {
        if (card.purchased) return;
        if (!window.confirm(`Do you want to buy this card (${card.tier})?`)) return;

        try {
            setLoading(true);
            const token = localStorage.getItem('auth_token');
            const tierValue = parseInt(card.tier.replace('£', ''), 10);
            
            const response = await fetch(`${API_URL}/api/customer/captain-tc-cards/buy`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ tier: tierValue })
            });

            const data = await response.json();
            if (response.ok && data.success) {
                alert('Card purchased successfully!');
                await fetchCardStatus(); // Refresh cards
            } else {
                alert(data.message || 'Failed to purchase card.');
            }
        } catch (err) {
            console.error('Error buying card:', err);
            alert('An error occurred during purchase.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCardStatus();
    }, [fetchCardStatus]);

    if (loading) {
        return (
            <div className="captain-tc-wrapper">
                <div className="captain-tc-loading">
                    <div className="captain-tc-spinner"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="captain-tc-wrapper">
            {/* Header */}
            <div className="captain-tc-header">
                <div className="captain-tc-header-left">
                    <span className="captain-tc-label">Captain TC</span>
                    <h2 className="captain-tc-title">Cash-Wallet Card</h2>
                </div>
            </div>

            {/* Card Tiers — Separate Cards */}
            <div className="captain-tc-cards-grid">
                {cards.map((card) => (
                    <div 
                        key={card.tier} 
                        className={`captain-tc-card-item ${card.purchased ? '' : 'cursor-pointer'}`}
                        onClick={() => handleBuy(card)}
                        style={{ cursor: card.purchased ? 'default' : 'pointer' }}
                    >
                        {/* Owned Header */}
                        <div className="captain-tc-card-owned">
                            Owned: <span className={card.purchased ? 'owned-yes' : 'owned-no'}>
                                {card.purchased ? 'Yes' : 'No'}
                            </span>
                        </div>

                        {/* Visual Card */}
                        <div className="captain-tc-card-body">
                            <img src={card.image} alt={`${card.tier} Captain TC Card`} className="captain-tc-actual-card-img" />
                        </div>

                        {/* Serial Footer */}
                        <div className="captain-tc-card-serial">
                            {card.purchased && card.serial ? (
                                <span>Serial : <strong>{card.serial}</strong></span>
                            ) : (
                                <span>&nbsp;</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Promo Wallets Section */}
            <div className="captain-tc-wallet-section" style={{ marginBottom: '32px' }}>
                <div className="captain-tc-wallet-row">
                    <div className="captain-tc-wallet-info">
                        <div className="captain-tc-wallet-icon">
                            <img src={promoTopCoinIcon} alt="TopCoin Promo Wallet" style={{ backgroundColor: "white" }} />
                        </div>
                        <div>
                            <h4 className="captain-tc-wallet-title">Captain TC Promotion TopCoin</h4>
                            <span className="captain-tc-wallet-balance">
                                {extraCardBalance.toLocaleString()} TC
                            </span>
                        </div>
                    </div>
                </div>

                <div className="captain-tc-wallet-row">
                    <div className="captain-tc-wallet-info">
                        <div className="captain-tc-wallet-icon">
                            <img src={promoCashIcon} alt="Cash Promo Wallet" style={{ backgroundColor: "white" }} />
                        </div>
                        <div>
                            <h4 className="captain-tc-wallet-title">Captain TC Promotional Cash Wallet</h4>
                            <span className="captain-tc-wallet-balance">
                                £ {(bonusCardBalance * 0.005).toFixed(2)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CaptainTCCard;
